import { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { Download, LogOut, Users, ArrowLeft, Trash2, AlertTriangle, Globe2, MessageCircle, Mail } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import WorldMap from "./WorldMap";
import GrowthChart from "./GrowthChart";
import { COUNTRIES } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COUNTRY_LABEL = COUNTRIES.reduce((acc, c) => {
  acc[c.code] = c.it;
  return acc;
}, {});

const ROLE_LABELS = {
  player: "Calciatore",
  parent: "Genitore",
  scout: "Scout / Osservatore",
  club: "Società sportiva",
  other: "Altro",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState(null);
  const [boost, setBoost] = useState(0);
  const [messages, setMessages] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [confirmEntry, setConfirmEntry] = useState(null);

  const token = localStorage.getItem("th_admin_token");
  const authHeaders = useCallback(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  const handleAuthError = useCallback(() => {
    localStorage.removeItem("th_admin_token");
    navigate("/admin/login");
  }, [navigate]);

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    axios
      .get(`${API}/admin/waitlist`, authHeaders())
      .then((r) => {
        setEntries(r.data.entries);
        setBoost(r.data.boost || 0);
      })
      .catch((e) => {
        if (e.response?.status === 401) handleAuthError();
        else setEntries([]);
      });
    axios
      .get(`${API}/admin/messages`, authHeaders())
      .then((r) => setMessages(r.data.messages))
      .catch(() => setMessages([]));
  }, [token, navigate, authHeaders, handleAuthError]);

  const markRead = async (id) => {
    try {
      await axios.post(`${API}/admin/messages/${id}/read`, {}, authHeaders());
      setMessages((prev) =>
        prev.map((m) => (m.id === id ? { ...m, read: true } : m))
      );
    } catch (e) {
      if (e.response?.status === 401) handleAuthError();
    }
  };

  const deleteMessage = async (id) => {
    try {
      await axios.delete(`${API}/admin/messages/${id}`, authHeaders());
      setMessages((prev) => prev.filter((m) => m.id !== id));
    } catch (e) {
      if (e.response?.status === 401) handleAuthError();
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/waitlist/export`, {
        ...authHeaders(),
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "talent-hub-waitlist.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (e.response?.status === 401) handleAuthError();
    } finally {
      setExporting(false);
    }
  };

  const removeEntry = async (id) => {
    setDeleting(id);
    try {
      await axios.delete(`${API}/admin/waitlist/${id}`, authHeaders());
      setEntries((prev) => prev.filter((e) => e.id !== id));
    } catch (e) {
      if (e.response?.status === 401) handleAuthError();
    } finally {
      setDeleting(null);
      setConfirmEntry(null);
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, authHeaders());
    } catch {}
    localStorage.removeItem("th_admin_token");
    navigate("/admin/login");
  };

  const byRole = (entries || []).reduce((acc, e) => {
    acc[e.role] = (acc[e.role] || 0) + 1;
    return acc;
  }, {});

  const byCountry = (entries || []).reduce((acc, e) => {
    const code = e.country || "OTHER";
    acc[code] = (acc[code] || 0) + 1;
    return acc;
  }, {});

  const countryList = Object.entries(byCountry).sort((a, b) => b[1] - a[1]);

  return (
    <div
      data-testid="admin-dashboard"
      className="min-h-screen bg-[#06080C] px-6 py-10 text-white"
    >
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-[#00F562]">
              <span className="font-display text-xs font-black leading-none tracking-tight text-[#06080C]">
                TH
              </span>
            </span>
            <h1 className="font-display text-lg font-extrabold uppercase tracking-widest">
              Report <span className="text-[#00F562]">Iscritti</span>
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="export-csv-button"
              onClick={exportCsv}
              disabled={exporting || !entries?.length}
              className="flex items-center gap-2 rounded-full bg-[#00F562] px-5 py-2.5 text-[11px] font-extrabold uppercase tracking-widest text-[#06080C] transition-all duration-300 hover:shadow-[0_0_25px_rgba(0,245,98,0.5)] disabled:opacity-50"
            >
              <Download size={13} />
              {exporting ? "Export…" : "Esporta CSV"}
            </button>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 text-[11px] font-bold uppercase tracking-widest text-slate-300 transition-colors duration-300 hover:border-red-400/60 hover:text-red-400"
            >
              <LogOut size={13} />
              Esci
            </button>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3">
          <div
            data-testid="admin-stat-total"
            className="flex items-center gap-3 rounded-xl border border-white/[0.08] bg-[#0D111A] px-5 py-4"
          >
            <Users size={18} className="text-[#00F562]" />
            <div>
              <p className="font-mono text-xl font-bold">
                {entries === null ? "—" : entries.length}
              </p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                Iscritti reali
              </p>
            </div>
          </div>
          {boost > 0 && (
            <div
              data-testid="admin-stat-public"
              className="flex items-center gap-3 rounded-xl border border-[#00F562]/25 bg-[#00F562]/[0.04] px-5 py-4"
            >
              <Users size={18} className="text-[#00F562]" />
              <div>
                <p className="font-mono text-xl font-bold text-[#00F562]">
                  {entries === null ? "—" : entries.length + boost}
                </p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
                  Mostrato al pubblico
                </p>
                <p className="mt-0.5 font-mono text-[9px] uppercase tracking-widest text-slate-500">
                  boost +{boost}
                </p>
              </div>
            </div>
          )}
          {Object.entries(byRole).map(([role, n]) => (
            <div
              key={role}
              data-testid={`admin-stat-role-${role}`}
              className="rounded-xl border border-white/[0.08] bg-[#0D111A] px-5 py-4"
            >
              <p className="font-mono text-xl font-bold text-[#00F562]">{n}</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                {ROLE_LABELS[role] || role}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-8">
          <GrowthChart entries={entries} />
        </div>

        {countryList.length > 0 && (
          <div
            data-testid="admin-geo-dashboard"
            className="mt-8 rounded-2xl border border-white/[0.08] bg-[#0D111A] p-6"
          >
            <div className="flex items-center gap-3">
              <Globe2 size={16} className="text-[#00F562]" />
              <h2 className="font-mono text-xs uppercase tracking-widest text-slate-400">
                Distribuzione geografica
              </h2>
              <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-slate-500">
                {countryList.length} {countryList.length === 1 ? "paese" : "paesi"}
              </span>
            </div>

            <div className="mt-5">
              <WorldMap byCountry={byCountry} />
            </div>

            <div className="mt-6 grid grid-cols-2 gap-x-6 gap-y-2 border-t border-white/[0.08] pt-5 sm:grid-cols-3 lg:grid-cols-5">
              {countryList.slice(0, 10).map(([code, count]) => (
                <div
                  key={code}
                  data-testid={`geo-row-${code}`}
                  className="flex items-center gap-2 py-1"
                >
                  <span className="w-9 font-mono text-[10px] uppercase tracking-widest text-slate-400">
                    {code}
                  </span>
                  <span className="flex-1 truncate text-xs text-slate-300">
                    {COUNTRY_LABEL[code] || code}
                  </span>
                  <span className="font-mono text-sm font-bold text-[#00F562] tabular-nums">
                    {count}
                  </span>
                </div>
              ))}
            </div>
            {countryList.length > 10 && (
              <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-slate-500">
                + {countryList.length - 10} altri paesi
              </p>
            )}
          </div>
        )}

        <div
          data-testid="admin-messages-section"
          className="mt-8 overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0D111A]"
        >
          <div className="flex items-center gap-3 border-b border-white/[0.08] px-6 py-4">
            <MessageCircle size={16} className="text-[#00F562]" />
            <h2 className="font-mono text-xs uppercase tracking-widest text-slate-400">
              Messaggi dalla chat
            </h2>
            <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-slate-500">
              {messages.length} tot. •{" "}
              <span className="text-[#00F562]">
                {messages.filter((m) => !m.read).length} non letti
              </span>
            </span>
          </div>
          {messages.length === 0 ? (
            <p className="p-10 text-center text-sm text-slate-500">
              Nessun messaggio ancora.
            </p>
          ) : (
            <ul className="divide-y divide-white/[0.05]">
              {messages.map((m) => (
                <li
                  key={m.id}
                  data-testid={`message-row-${m.id}`}
                  className={`px-6 py-4 transition-colors duration-300 ${
                    !m.read ? "bg-[#00F562]/[0.03]" : ""
                  }`}
                >
                  <div className="flex flex-wrap items-baseline gap-3">
                    {!m.read && (
                      <span className="h-2 w-2 flex-shrink-0 rounded-full bg-[#00F562]" />
                    )}
                    <span className="font-display text-sm font-extrabold uppercase tracking-wide text-white">
                      {m.name || "(senza nome)"}
                    </span>
                    <a
                      href={`mailto:${m.email}?subject=Re: Talent Hub`}
                      className="font-mono text-xs text-[#00F562] hover:underline"
                    >
                      {m.email}
                    </a>
                    <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-slate-500">
                      {new Date(m.created_at).toLocaleDateString("it-IT", {
                        day: "2-digit",
                        month: "short",
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                  </div>
                  <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-slate-300">
                    {m.message}
                  </p>
                  <div className="mt-3 flex items-center gap-2">
                    <a
                      data-testid={`message-reply-${m.id}`}
                      href={`mailto:${m.email}?subject=${encodeURIComponent(
                        "Re: Talent Hub"
                      )}`}
                      className="inline-flex items-center gap-1.5 rounded-full border border-[#00F562]/40 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-[#00F562] transition-colors duration-300 hover:bg-[#00F562] hover:text-[#06080C]"
                    >
                      <Mail size={11} />
                      Rispondi
                    </a>
                    {!m.read && (
                      <button
                        data-testid={`message-read-${m.id}`}
                        type="button"
                        onClick={() => markRead(m.id)}
                        className="inline-flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-slate-300 transition-colors duration-300 hover:border-white/30 hover:text-white"
                      >
                        Segna letto
                      </button>
                    )}
                    <button
                      data-testid={`message-delete-${m.id}`}
                      type="button"
                      onClick={() => deleteMessage(m.id)}
                      aria-label="Elimina messaggio"
                      className="ml-auto inline-flex items-center justify-center rounded-full border border-white/10 p-1.5 text-slate-500 transition-colors duration-300 hover:border-red-400/60 hover:text-red-400"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="mt-8 overflow-hidden rounded-2xl border border-white/[0.08] bg-[#0D111A]">
          {entries === null ? (
            <p className="p-10 text-center text-sm text-slate-500">
              Caricamento…
            </p>
          ) : entries.length === 0 ? (
            <p
              data-testid="waitlist-empty-message"
              className="p-10 text-center text-sm text-slate-500"
            >
              Nessun iscritto ancora.
            </p>
          ) : (
            <table data-testid="waitlist-table" className="w-full text-left">
              <thead>
                <tr className="border-b border-white/[0.08] font-mono text-[10px] uppercase tracking-widest text-slate-500">
                  <th className="px-5 py-4">Email</th>
                  <th className="px-5 py-4">Ruolo</th>
                  <th className="px-5 py-4">Paese</th>
                  <th className="px-5 py-4">Lingua</th>
                  <th className="px-5 py-4">Data iscrizione</th>
                  <th className="px-5 py-4 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => (
                  <tr
                    key={e.id}
                    data-testid={`waitlist-row-${e.id}`}
                    className="border-b border-white/[0.04] text-sm transition-colors duration-200 hover:bg-white/[0.02]"
                  >
                    <td className="px-5 py-3.5 text-slate-200">{e.email}</td>
                    <td className="px-5 py-3.5">
                      <span className="rounded-full border border-[#00F562]/30 bg-[#00F562]/10 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-[#00F562]">
                        {ROLE_LABELS[e.role] || e.role}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 font-mono text-xs uppercase text-slate-300">
                      {e.country || "—"}
                    </td>
                    <td className="px-5 py-3.5 font-mono text-xs uppercase text-slate-400">
                      {e.lang}
                    </td>
                    <td className="px-5 py-3.5 font-mono text-xs text-slate-500">
                      {new Date(e.created_at).toLocaleString("it-IT")}
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <button
                        data-testid={`delete-entry-${e.id}`}
                        onClick={() => setConfirmEntry(e)}
                        disabled={deleting === e.id}
                        aria-label={`Elimina ${e.email}`}
                        className="inline-flex items-center justify-center rounded-full border border-white/10 p-2 text-slate-500 transition-all duration-300 hover:border-red-400/60 hover:text-red-400 disabled:opacity-40"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <Link
          data-testid="admin-back-landing-link"
          to="/"
          className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 transition-colors duration-300 hover:text-[#00F562]"
        >
          <ArrowLeft size={13} />
          Torna alla landing
        </Link>
      </div>

      <AlertDialog
        open={!!confirmEntry}
        onOpenChange={(open) => !open && setConfirmEntry(null)}
      >
        <AlertDialogContent
          data-testid="delete-confirm-dialog"
          className="border-white/10 bg-[#0D111A] text-white"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 font-display uppercase tracking-widest">
              <span className="flex h-8 w-8 items-center justify-center rounded-md bg-red-500/15 text-red-400">
                <AlertTriangle size={16} />
              </span>
              Sei sicuro?
            </AlertDialogTitle>
            <AlertDialogDescription className="pt-2 text-sm text-slate-400">
              Stai per eliminare{" "}
              <span className="font-mono text-slate-200">
                {confirmEntry?.email}
              </span>{" "}
              dalla waitlist. L'operazione è definitiva e non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel
              data-testid="delete-cancel-button"
              disabled={!!deleting}
              className="rounded-full border-white/15 bg-transparent px-5 text-[11px] font-bold uppercase tracking-widest text-slate-300 hover:bg-white/5 hover:text-white"
            >
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm-button"
              disabled={!!deleting}
              onClick={(evt) => {
                evt.preventDefault();
                if (confirmEntry) removeEntry(confirmEntry.id);
              }}
              className="rounded-full bg-red-500 px-5 text-[11px] font-extrabold uppercase tracking-widest text-white hover:bg-red-600 focus:ring-red-500"
            >
              {deleting ? "Elimino…" : "Elimina"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
